/**
 * budgetExcelParser.js
 * Parses budget Excel workbooks (e.g. 1-Budjetti 2026.xlsx) into structured Firestore documents.
 * Extracts cost center worksheets, categories, accounts, budgeted estimates (ARVIO),
 * and historical actuals (TOTEUTUMA as baseToteutuma).
 * Also supports generating a clean blank budget structure from scratch.
 */

import * as XLSX from "xlsx";
import { COST_CENTERS } from "./budgetCsvParser.js";

export const DEFAULT_CATEGORIES = [
  "Tuotot",
  "Materiaalit ja palvelut",
  "Henkilöstökulut",
  "Ajoneuvokulut",
  "Kone- ja kalustokulut",
  "Muut hallintokulut",
  "Varsinaisen toiminnan kulut",
  "Investoinnit",
];

/**
 * Creates a clean default budget structure for any year from scratch
 */
export function createDefaultBudgetStructure(year = new Date().getFullYear()) {
  const projects = COST_CENTERS.map((cc) => ({
    id: cc.id,
    code: cc.code,
    name: cc.name,
    fullName: cc.fullName,
    accounts: [
      {
        id: `acc_${cc.code}_tuotot_1`,
        accountNumber: "3000",
        accountName: "Varsinaisen toiminnan tuotot",
        subAccount: "3000",
        category: "Tuotot",
        type: "income",
        arvio: 0,
        baseToteutuma: 0,
        isTotal: false,
        isCustom: false,
      },
      {
        id: `acc_${cc.code}_kulut_1`,
        accountNumber: "4001",
        accountName: "Muut opinto/koul.toiminnan kulu",
        subAccount: "3073",
        category: "Materiaalit ja palvelut",
        type: "expense",
        arvio: 0,
        baseToteutuma: 0,
        isTotal: false,
        isCustom: false,
      },
      {
        id: `acc_${cc.code}_kulut_2`,
        accountNumber: "7710",
        accountName: "Kone- ja kalustokulut",
        subAccount: "3984",
        category: "Kone- ja kalustokulut",
        type: "expense",
        arvio: 0,
        baseToteutuma: 0,
        isTotal: false,
        isCustom: false,
      },
      {
        id: `acc_${cc.code}_kulut_3`,
        accountNumber: "8450",
        accountName: "Posti, puhelin, internet",
        subAccount: "3970",
        category: "Muut hallintokulut",
        type: "expense",
        arvio: 0,
        baseToteutuma: 0,
        isTotal: false,
        isCustom: false,
      },
    ],
  }));

  return {
    year: parseInt(year, 10),
    title: `NOORMARKUN VPK, TALOUSARVIO VUODELLE ${year}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    source: "scratch",
    projects,
  };
}

/**
 * Parses an Excel file (ArrayBuffer or File)
 */
export async function parseBudgetExcel(fileOrBuffer, customYear = null) {
  let buffer;
  let fileName = "";
  if (fileOrBuffer instanceof Blob || fileOrBuffer instanceof File) {
    fileName = fileOrBuffer.name || "";
    buffer = await fileOrBuffer.arrayBuffer();
  } else {
    buffer = fileOrBuffer;
  }

  const wb = XLSX.read(buffer, { type: "array" });

  // Detect Year
  let detectedYear = customYear;
  if (!detectedYear && fileName) {
    const m = fileName.match(/20\d{2}/);
    if (m) detectedYear = parseInt(m[0], 10);
  }

  const projects = [];

  for (const sheetName of wb.SheetNames) {
    if (sheetName.toLowerCase().includes("op ")) {
      // Optional investment sheet
      continue;
    }

    const ws = wb.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
    if (!data || data.length === 0) continue;

    const cleanName = sheetName.replace(/[.\s]+$/, "").trim();

    // Check header info for year if not detected
    if (!detectedYear) {
      data.slice(0, 15).forEach((r) => {
        if (r && r[1] && typeof r[1] === "string") {
          const ym = r[1].match(/20\d{2}/);
          if (ym) detectedYear = parseInt(ym[0], 10);
        }
      });
    }

    // Find Kp code and name
    let kpCode = "";
    let kpName = cleanName;
    data.slice(0, 20).forEach((r) => {
      if (r && r[0] && typeof r[0] === "string") {
        const m = r[0].trim().match(/^(\d{2})\s*(.*)$/);
        if (m) {
          kpCode = m[1];
          if (m[2]) kpName = m[2].trim();
        }
      }
    });

    // Fallback match by sheet name if kpCode not found
    if (!kpCode) {
      const foundCc = COST_CENTERS.find(
        (c) => c.name.toLowerCase() === cleanName.toLowerCase(),
      );
      if (foundCc) {
        kpCode = foundCc.code;
        kpName = foundCc.name;
      }
    }

    const headerIdx = data.findIndex(
      (r) => r && r.some((c) => typeof c === "string" && c.includes("ARVIO")),
    );
    if (headerIdx === -1) continue;

    let currentCategory = "Yleinen";
    let isInvestmentsSection = false;
    let investmentSubProject = "";
    const accounts = [];

    for (let i = headerIdx + 1; i < data.length; i++) {
      const r = data[i];
      if (!r || r.length === 0) continue;

      const b = r[1]; // Procountor tili or header code
      const c = r[2]; // Description / Account name / Category
      const d = r[3]; // Internal sub-account code (Kirjanpidon tili)
      const arvio = r[4]; // ARVIO
      const toteutuma = r[5]; // TOTEUTUMA

      // Check if entering the Investoinnit main section
      if (
        (c && String(c).trim().toLowerCase() === "investoinnit") ||
        (b === "XXX" &&
          c &&
          String(c).trim().toLowerCase().includes("investoin"))
      ) {
        isInvestmentsSection = true;
        investmentSubProject = "";
        currentCategory = "Investoinnit";
      }

      // Identify category header lines (e.g. 300 Myyntituotot, 400 Materiaalit ja palvelut, etc.)
      const isCategoryHeader =
        b !== undefined &&
        c &&
        (arvio === undefined || arvio === null) &&
        (toteutuma === undefined || toteutuma === null || toteutuma === 0) &&
        ((typeof b === "number" && b < 1000) ||
          (typeof b === "string" && b.startsWith("X")));

      if (isCategoryHeader) {
        if (isInvestmentsSection) {
          const bStr = typeof b === "string" ? b.trim() : "";
          if (
            /^XX\d+/i.test(bStr) ||
            (/^XX[0-9A-Za-z]/i.test(bStr) && !bStr.startsWith("XXX"))
          ) {
            investmentSubProject = `${bStr} ${String(c).trim()}`.trim();
            currentCategory = `Investoinnit (${investmentSubProject})`;
          }
        } else {
          currentCategory = String(c).trim();
        }
      }

      const isTotal = Boolean(
        (c && String(c).toLowerCase().includes("yhteensä")) ||
          (d && String(d).toLowerCase().includes("yhteensä")),
      );

      if (
        c &&
        (b !== undefined || arvio !== undefined || toteutuma !== undefined)
      ) {
        const accNum = b !== undefined ? String(b).trim() : "";
        const accName = String(c).trim();
        const subAcc =
          d !== undefined && typeof d !== "string"
            ? String(d).trim()
            : typeof d === "string" && !d.includes("yhteensä")
            ? d.trim()
            : "";

        const arvioVal = typeof arvio === "number" ? arvio : 0;
        const toteutumaVal = typeof toteutuma === "number" ? toteutuma : 0;

        let type = "expense";
        if (
          isInvestmentsSection ||
          currentCategory.toLowerCase().includes("investoin") ||
          (typeof accNum === "string" && accNum.startsWith("X"))
        ) {
          type = "investment";
        } else if (
          currentCategory.toLowerCase().includes("tase") ||
          currentCategory.toLowerCase().includes("pääoma") ||
          currentCategory.toLowerCase().includes("voitto (tappio)") ||
          (accNum && (accNum.startsWith("1") || accNum.startsWith("2")))
        ) {
          type = "balance";
        } else if (
          currentCategory.toLowerCase().includes("varainhankinnan kulu") ||
          accNum.startsWith("905") ||
          accNum.startsWith("906") ||
          accNum.startsWith("908")
        ) {
          type = "expense";
        } else if (
          currentCategory.toLowerCase().includes("tuoto") ||
          accNum.startsWith("3") ||
          accNum.startsWith("9")
        ) {
          type = "income";
        }

        const isTitle = Boolean(
          (accNum && /^\d{1,3}$/.test(accNum)) ||
            accNum === "XXX" ||
            (accNum && /^XX\d?$/.test(accNum)) ||
            accNum === "X00",
        );
        const isModifiable = !isTitle && !isTotal;

        accounts.push({
          id: `acc_${kpCode || cleanName}_${accNum || "x"}_${i}`,
          accountNumber: accNum,
          accountName: accName,
          subAccount: subAcc,
          category: currentCategory,
          type,
          investmentGroup: isInvestmentsSection ? investmentSubProject : "",
          arvio: Math.round(arvioVal * 100) / 100,
          baseToteutuma: Math.round(toteutumaVal * 100) / 100,
          isTotal,
          isTitle,
          isHeader: isTitle,
          isModifiable,
          isCustom: false,
        });
      }
    }

    projects.push({
      id: kpCode || cleanName,
      code: kpCode,
      name: kpName,
      fullName: kpCode ? `${kpCode} ${kpName}` : kpName,
      accounts,
    });
  }

  // Sort projects according to standard cost center order (10, 20, 30, 40, 50, 60, 70)
  projects.sort((a, b) => {
    const numA = parseInt(a.code, 10) || 999;
    const numB = parseInt(b.code, 10) || 999;
    return numA - numB;
  });

  const year = detectedYear || 2026;

  return {
    year,
    title: `NOORMARKUN VPK, TALOUSARVIO VUODELLE ${year}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    source: fileName || "excel",
    projects,
  };
}
